/* Coverage by quality dimension, for the Suggestions screen and Auto Suggest. Pure — no React.
 *
 * **One answer per dimension, and "we do not know" is one of the answers.** The server fills every
 * dimension a rule can measure with a recommendation, a template, or a stated gap — so on a run that
 * returned its gaps, a dimension with none of those is genuinely covered already: an existing rule on
 * the table made the recommendation a duplicate, or a person decided on it earlier. That inference
 * holds only while the gaps are known. A stored run does not keep them (`GenerationRun` has no
 * `coverage_gaps`), so after a reload the same empty dimension is `unknown` rather than `covered` —
 * claiming coverage the screen cannot see is the absence-is-not-zero defect in a new costume.
 */

import type { AutoSuggestRecommendation, CoverageGap, Suggestion } from '../api/rulegen';

/** The six dimensions a rule kind measures, then accuracy, which none does. The order a quality
 * screen lists them in, matching the server's `DIMENSIONS` plus its always-present accuracy gap. */
export const DIMENSIONS = [
  'completeness',
  'uniqueness',
  'validity',
  'timeliness',
  'consistency',
  'integrity',
  'accuracy',
] as const;

export type Dimension = (typeof DIMENSIONS)[number];

/** Where one dimension stands, strongest first. */
export type CoverageState =
  /** A rule is bound on this table and checking it. */
  | 'in_place'
  /** A rule was drafted from a recommendation and is waiting for approval. */
  | 'in_progress'
  /** At least one ready recommendation is waiting for a decision. */
  | 'offered'
  /** Only templates: a recommendation exists but a person must supply its values first. */
  | 'template'
  /** Every recommendation here was dismissed. */
  | 'declined'
  /** The server said no rule is possible, and why. */
  | 'gap'
  /** Nothing to add: an existing rule or an earlier decision answers it. Only on a run with gaps. */
  | 'covered'
  /** The run's gaps are not known, so an empty dimension cannot be called covered. */
  | 'unknown';

/** One recommendation reduced to what coverage needs, whichever surface it came from. */
export interface CoverageItem {
  readonly category: string;
  readonly template: boolean;
  readonly phase: 'open' | 'in_progress' | 'in_place' | 'declined';
}

export interface DimensionCoverage {
  readonly dimension: Dimension;
  readonly state: CoverageState;
  /** How many items put the dimension in its state — e.g. three ready recommendations. */
  readonly count: number;
  /** The server's sentence, for a gap. Host-written, never a data value. */
  readonly reason: string | null;
}

export function itemsFromSuggestions(suggestions: readonly Suggestion[]): CoverageItem[] {
  return suggestions.map((item) => ({
    category: item.category,
    template: item.needs_input.length > 0,
    phase:
      item.status === 'bound'
        ? 'in_place'
        : item.status === 'drafted'
          ? 'in_progress'
          : item.status === 'dismissed'
            ? 'declined'
            : 'open',
  }));
}

/** Auto Suggest lists no dismissed rows — the preview only counts them — so nothing here declines. */
export function itemsFromRecommendations(
  recommendations: readonly AutoSuggestRecommendation[],
): CoverageItem[] {
  return recommendations.map((item) => ({
    category: item.category,
    template: item.needs_input.length > 0,
    phase:
      item.action === 'already_bound'
        ? 'in_place'
        : item.action === 'awaiting_approval'
          ? 'in_progress'
          : 'open',
  }));
}

/** The readout, one entry per dimension in `DIMENSIONS` order.
 *
 * `gaps` is `null` when the run's gaps are not known, which is what turns an empty dimension into
 * `unknown` instead of `covered`. A recorded decision outranks a gap because it is a fact about this
 * table, where a gap is a statement about what the generator could do.
 */
export function coverageByDimension(
  items: readonly CoverageItem[],
  gaps: readonly CoverageGap[] | null,
): readonly DimensionCoverage[] {
  return DIMENSIONS.map((dimension) => {
    const here = items.filter((item) => item.category === dimension);
    const count = (predicate: (item: CoverageItem) => boolean): number =>
      here.filter(predicate).length;
    const gap = gaps?.find((entry) => entry.dimension === dimension) ?? null;

    const ranked: readonly [CoverageState, number][] = [
      ['in_place', count((item) => item.phase === 'in_place')],
      ['in_progress', count((item) => item.phase === 'in_progress')],
      ['offered', count((item) => item.phase === 'open' && !item.template)],
      ['template', count((item) => item.phase === 'open' && item.template)],
      ['declined', count((item) => item.phase === 'declined')],
    ];
    const found = ranked.find(([, n]) => n > 0);
    if (found !== undefined) {
      return { dimension, state: found[0], count: found[1], reason: null };
    }
    if (gap !== null) return { dimension, state: 'gap', count: 0, reason: gap.reason };
    return {
      dimension,
      state: gaps === null ? 'unknown' : 'covered',
      count: 0,
      reason: null,
    };
  });
}

/** Chip tone and whether its dot is filled. Total over `CoverageState`, so a ninth state is a
 * compile error rather than a grey chip. **Filled means a rule exists or is being made**; a hollow
 * ring means nothing has happened yet, which keeps the two apart in greyscale. */
export const COVERAGE_LOOK = {
  in_place: { tone: 'ok', filled: true },
  in_progress: { tone: 'warn', filled: true },
  offered: { tone: 'info', filled: false },
  template: { tone: 'info', filled: false },
  declined: { tone: 'unknown', filled: false },
  gap: { tone: 'unknown', filled: false },
  covered: { tone: 'ok', filled: false },
  unknown: { tone: 'unknown', filled: false },
} as const satisfies Record<
  CoverageState,
  { readonly tone: string; readonly filled: boolean }
>;

/** Whether a dimension's cell can narrow the list to its recommendations. Only where there are
 * recommendations to show: a gap or an unknown has nothing behind it, and accuracy never does. */
export function isFilterable(entry: DimensionCoverage): boolean {
  return entry.count > 0 && entry.state !== 'gap';
}
